import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Check, Loader2, GraduationCap } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Position } from "@/services/positionService";
import {
  positionRequestService,
  UserPositionRequestDTO,
} from "@/services/positionRequestService";

// One shared empty list for "no requests yet". A fresh `[]` default was a new
// array on every render, which re-ran the effect below and re-rendered forever
// whenever the modal had no data, including the whole time it sits closed.
const NO_REQUESTS: UserPositionRequestDTO[] = [];

interface PositionRequestModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  availablePositions: Position[];
}

export function PositionRequestModal({
  open,
  onOpenChange,
  availablePositions,
}: PositionRequestModalProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [requestedPositions, setRequestedPositions] = useState<Set<string>>(
    new Set(),
  );

  // Positions are now passed as props, so we just use loading state for user requests
  const positionsLoading = false;

  const { data: userRequests = NO_REQUESTS } = useQuery({
    queryKey: ["position-requests", "my"],
    queryFn: positionRequestService.getMyPositionRequests,
    enabled: open,
  });

  // Update requested positions set when user requests change
  useEffect(() => {
    const requestedIds = new Set(userRequests.map((req) => req.positionId));
    setRequestedPositions(requestedIds);
  }, [userRequests]);

  const createMutation = useMutation({
    mutationFn: positionRequestService.createPositionRequest,
    onSuccess: (data) => {
      setRequestedPositions((prev) => new Set([...prev, data.positionId]));
      queryClient.invalidateQueries({ queryKey: ["position-requests"] });
      toast({
        title: "Position requested successfully",
        description: `You've requested to learn ${data.positionTableName}`,
        variant: "success",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error requesting position",
        description: error.message || "Failed to request position",
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: positionRequestService.deletePositionRequest,
    onSuccess: (_, deletedId) => {
      const deletedRequest = userRequests.find((req) => req.id === deletedId);
      if (deletedRequest) {
        setRequestedPositions((prev) => {
          const newSet = new Set(prev);
          newSet.delete(deletedRequest.positionId);
          return newSet;
        });
      }
      queryClient.invalidateQueries({ queryKey: ["position-requests"] });
      toast({
        title: "Position request removed",
        description: "Your position request has been removed",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error removing request",
        description: "Failed to remove position request",
        variant: "destructive",
      });
    },
  });

  const handlePositionClick = (position: Position) => {
    const isRequested = requestedPositions.has(position.id);

    if (isRequested) {
      // Find the request ID and delete it
      const request = userRequests.find(
        (req) => req.positionId === position.id,
      );
      if (request) {
        deleteMutation.mutate(request.id);
      }
    } else {
      // Create new request
      createMutation.mutate({ positionId: position.id });
    }
  };

  const isLoading = createMutation.isPending || deleteMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 text-xl">
            <div className="rounded-full bg-primary/10 p-2">
              <GraduationCap className="h-5 w-5 text-primary" />
            </div>
            Request Position Training
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          <p className="text-muted-foreground text-left">
            Below are all the positions you are not yet passed off on. Select
            any you would like to learn.
          </p>

          {positionsLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary mb-3" />
              <p className="text-sm text-muted-foreground">
                Loading available positions...
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {availablePositions.map((position) => {
                const isRequested = requestedPositions.has(position.id);
                return (
                  <div
                    key={position.id}
                    className={`relative p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 hover:scale-105 ${
                      isRequested
                        ? "border-success  shadow-md"
                        : "border-border hover:border-border hover:shadow-sm"
                    } ${isLoading ? "opacity-50 cursor-not-allowed" : ""}`}
                    onClick={() => !isLoading && handlePositionClick(position)}
                  >
                    <div className="flex items-center justify-center min-h-[60px]">
                      <span
                        className={`text-sm font-medium text-center leading-tight ${
                          isRequested ? "text-success" : "text-foreground"
                        }`}
                      >
                        {position.tableName}
                      </span>
                    </div>

                    {isRequested && (
                      <div className="absolute -top-2 -right-2 w-6 h-6 bg-success rounded-full flex items-center justify-center shadow-lg">
                        <Check className="h-3 w-3 text-success-foreground" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {!positionsLoading && availablePositions.length === 0 && (
            <div className="text-center py-12">
              <div className="w-16 h-16 mx-auto mb-4 bg-muted rounded-full flex items-center justify-center">
                <GraduationCap className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-medium text-foreground mb-2">
                No positions available
              </h3>
              <p className="text-sm text-muted-foreground">
                All available positions have been requested or you're already
                certified.
              </p>
            </div>
          )}

          {!positionsLoading && userRequests.length > 0 && (
            <div className=" border border-border rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-muted rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-muted-foreground font-semibold text-sm">
                    {userRequests.length}
                  </span>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    {userRequests.length === 1
                      ? "Position request pending"
                      : `${userRequests.length} position requests pending`}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Staff will contact you to schedule training sessions.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
